// domBuilders.js

import { getPublicAssetUrl } from "./getPublicAssetUrl";

export const PDF_CAPTURE_WIDTH = 1200;

function rewriteCssUrls(cssText, baseHref) {
  if (!cssText || !baseHref) {
    return cssText;
  }

  return cssText.replace(
    /url\((\s*['"]?)([^'")]+)(['"]?\s*)\)/g,
    (match, prefix, url, suffix) => {
      const trimmed = url.trim();
      if (!trimmed || /^(https?:|data:|blob:|#)/i.test(trimmed)) {
        return match;
      }

      try {
        const absolute = new URL(trimmed, baseHref).href;
        return `url(${prefix}${absolute}${suffix})`;
      } catch (error) {
        return match;
      }
    }
  );
}

/**
 * Serializes every readable (same-origin) stylesheet in the current document
 * into a single CSS string.
 *
 * Production builds serve the app CSS via an external <link> with a relative
 * href (homepage "."). html2canvas clones the DOM into an isolated iframe, and
 * that relative stylesheet fails to re-resolve on deep routes, leaving the
 * capture unstyled (flattened header, missing KPI cards). Inlining the actual
 * rules into the clone guarantees the PDF is styled exactly like the on-screen
 * UI, in every environment.
 */
export function collectDocumentCssText() {
  const parts = [];
  const sheets = Array.from(document.styleSheets || []);

  sheets.forEach((sheet) => {
    let rules;
    try {
      rules = sheet.cssRules;
    } catch (error) {
      // Cross-origin stylesheet (e.g. Google Fonts): its rules can't be read,
      // but its absolute <link> href still resolves fine inside the clone.
      return;
    }

    if (!rules) {
      return;
    }

    const baseHref = sheet.href || window.location.href;
    Array.from(rules).forEach((rule) => {
      parts.push(rewriteCssUrls(rule.cssText, baseHref));
    });
  });

  return parts.join("\n");
}

/**
 * Injects all readable document CSS into an html2canvas clone document so the
 * capture renders with the real application styles in production builds.
 */
export function inlineDocumentStylesIntoClone(clonedDoc) {
  if (!clonedDoc) {
    return;
  }

  try {
    const cssText = collectDocumentCssText();
    if (!cssText) {
      console.warn(
        "PDF export: no readable stylesheet rules were found to inline. " +
          "The exported PDF may be unstyled."
      );
      return;
    }

    const style = clonedDoc.createElement("style");
    style.setAttribute("data-pdf-inlined-styles", "true");
    style.textContent = cssText;
    (clonedDoc.head || clonedDoc.documentElement).appendChild(style);
  } catch (error) {
    console.error("PDF export: failed to inline document styles:", error);
  }
}

export function buildPDFWrapper(
  padding = { top: 0, right: 20, bottom: 20, left: 20 }
) {
  const wrapper = document.createElement("div");
  wrapper.style.display = "block";
  wrapper.style.width = "1200px"; // Fixed width for consistent PDF rendering
  wrapper.style.paddingTop = `${padding.top}px`;
  wrapper.style.paddingRight = `${padding.right}px`;
  wrapper.style.paddingBottom = `${padding.bottom}px`;
  wrapper.style.paddingLeft = `${padding.left}px`;
  wrapper.style.boxSizing = "border-box";
  return wrapper;
}

export function buildHeaderContainer() {
  const headerContainer = document.createElement("div");
  headerContainer.style.display = "flex";
  headerContainer.style.justifyContent = "space-between";
  headerContainer.style.alignItems = "center";
  headerContainer.style.marginBottom = "20px";
  headerContainer.style.paddingLeft = "40px";
  headerContainer.style.paddingRight = "40px";
  headerContainer.style.paddingTop = "0px";
  return headerContainer;
}

export function buildHeaderImage(src) {
  const img = document.createElement("img");
  img.src = src;
  img.style.width = "576px";
  img.style.height = "80px";
  img.style.marginTop = "20px";
  return img;
}

export function buildDateTimeElement(dateTimeString) {
  const dateTime = document.createElement("div");
  dateTime.textContent = dateTimeString;
  dateTime.style.color = "var(--black-40, rgba(28, 28, 28, 0.40))";
  dateTime.style.textAlign = "right";
  dateTime.style.fontFamily = "Archivo";
  dateTime.style.fontSize = "18px";
  dateTime.style.fontStyle = "normal";
  dateTime.style.fontWeight = "400";
  dateTime.style.lineHeight = "22px";
  dateTime.style.marginTop = "20px";
  dateTime.style.paddingRight = "40px";
  return dateTime;
}

export function buildFiltersContainer(filtersElement) {
  const filtersContainer = document.createElement("div");
  filtersContainer.style.paddingTop = "30px";
  filtersContainer.style.paddingBottom = "30px";
  filtersContainer.style.margin = "0 80px";
  filtersContainer.appendChild(filtersElement.cloneNode(true));
  return filtersContainer;
}

export function buildTableContainer(tableElement) {
  const tableContainer = document.createElement("div");
  tableContainer.style.display = "flex";
  tableContainer.style.justifyContent = "center";
  tableContainer.style.alignItems = "flex-start";
  tableContainer.style.marginTop = "20px";
  tableContainer.style.width = "100%";
  tableContainer.style.paddingLeft = "40px";
  tableContainer.style.overflow = "visible";

  const clonedTable = tableElement.cloneNode(true);
  clonedTable.style.transform = "none";
  clonedTable.style.transformOrigin = "initial";

  if (clonedTable.style.width === "125%") {
    clonedTable.style.width = "auto";
  }

  tableContainer.appendChild(clonedTable);
  return tableContainer;
}

export function buildContentContainer(element) {
  const contentContainer = document.createElement("div");
  contentContainer.style.display = "block";
  contentContainer.style.width = "100%";
  contentContainer.style.overflow = "visible";
  contentContainer.style.height = "auto";

  const clonedContent = element.cloneNode(true);
  clonedContent.style.transform = "none";
  clonedContent.style.transformOrigin = "initial";
  clonedContent.style.width = "100%";
  clonedContent.style.maxWidth = "100%";
  clonedContent.style.height = "auto";
  clonedContent.style.overflow = "visible";

  clonedContent
    .querySelectorAll(".BoxTD")
    .forEach((node) => {
      node.style.transform = "none";
      node.style.width = "100%";
      node.style.height = "auto";
      node.style.minHeight = "auto";
      node.style.overflow = "visible";
    });

  fixPublicAssetPaths(clonedContent);
  sanitizeBrokenImagesForPdf(clonedContent);

  clonedContent
    .querySelectorAll(
      ".TDHeaderExportButton, .ViewMoreButtonContainer, .TCSelectedTeamCardRemove, .TCAddTeamPlaceholder, .TCAddTeamSearch, .TCControlsSearch"
    )
    .forEach((node) => node.remove());

  contentContainer.appendChild(clonedContent);
  return contentContainer;
}

export function preparePdfCaptureClone(root) {
  if (!root) {
    return;
  }

  fixPublicAssetPaths(root);
  sanitizeBrokenImagesForPdf(root);

  root
    .querySelectorAll(
      ".TDHeaderExportButton, .ViewMoreButtonContainer, .TCSelectedTeamCardRemove, .TCAddTeamPlaceholder, .TCAddTeamSearch, .TCControlsSearch"
    )
    .forEach((node) => node.remove());
}

function applyFixedFlagStyles(element, width, height) {
  element.style.width = width;
  element.style.height = height;
  element.style.minWidth = width;
  element.style.maxWidth = width;
  element.style.minHeight = height;
  element.style.maxHeight = height;
  element.style.objectFit = "cover";
  element.style.flexShrink = "0";

  if (element.tagName === "IMG") {
    element.style.display = "block";
  }
}

export function ensureExportCaptureWrapper(clonedDoc, clonedElement) {
  if (!clonedElement || !clonedElement.parentNode) {
    return clonedElement;
  }

  if (clonedElement.classList.contains("TDExportCapture--exporting")) {
    clonedElement.style.width = `${PDF_CAPTURE_WIDTH}px`;
    clonedElement.style.maxWidth = `${PDF_CAPTURE_WIDTH}px`;
    clonedElement.style.boxSizing = "border-box";
    return clonedElement;
  }

  const wrapper = clonedDoc.createElement("div");
  wrapper.className = "TDExportCapture TDExportCapture--exporting";
  wrapper.setAttribute("data-pdf-export-mode", "true");
  wrapper.style.width = `${PDF_CAPTURE_WIDTH}px`;
  wrapper.style.maxWidth = `${PDF_CAPTURE_WIDTH}px`;
  wrapper.style.boxSizing = "border-box";
  wrapper.style.background = "#ffffff";

  clonedElement.parentNode.insertBefore(wrapper, clonedElement);
  wrapper.appendChild(clonedElement);
  return wrapper;
}

export function prepareTeamHistoryPdfCaptureClone(root) {
  preparePdfCaptureClone(root);

  if (!root) {
    return;
  }

  root.style.width = `${PDF_CAPTURE_WIDTH}px`;
  root.style.maxWidth = `${PDF_CAPTURE_WIDTH}px`;
  root.style.boxSizing = "border-box";
  root.style.background = "#ffffff";

  root.querySelectorAll(".BoxTD").forEach((node) => {
    node.style.transform = "none";
    node.style.width = "100%";
    node.style.height = "auto";
    node.style.minHeight = "auto";
    node.style.overflow = "visible";
  });

  root.querySelectorAll("img.Flag").forEach((img) => {
    applyFixedFlagStyles(img, "114px", "80px");
  });

  root.querySelectorAll("img.FlagSmallIcon").forEach((img) => {
    applyFixedFlagStyles(img, "24px", "16px");
  });

  root.querySelectorAll(".TeamFlagPlaceholder--header").forEach((node) => {
    applyFixedFlagStyles(node, "114px", "80px");
  });

  root.querySelectorAll(".TeamFlagPlaceholder--small").forEach((node) => {
    applyFixedFlagStyles(node, "24px", "16px");
  });

  root.querySelectorAll(".OuterHeader").forEach((node) => {
    node.style.display = "flex";
    node.style.flexDirection = "row";
    node.style.justifyContent = "space-between";
    node.style.alignItems = "flex-start";
    node.style.width = "100%";
    node.style.background = "#221B70";
    node.style.color = "#ffffff";
    node.style.padding = "48px";
    node.style.gap = "32px";
    node.style.boxSizing = "border-box";
  });

  root.querySelectorAll(".HeaderLeft").forEach((node) => {
    node.style.display = "flex";
    node.style.flexDirection = "row";
    node.style.alignItems = "flex-start";
    node.style.gap = "40px";
    node.style.flex = "1 1 auto";
  });

  root.querySelectorAll(".Header2").forEach((node) => {
    node.style.display = "flex";
    node.style.flexDirection = "column";
    node.style.alignSelf = "flex-start";
    node.style.flexShrink = "0";
  });

  root.querySelectorAll(".ExtraRowTD").forEach((node) => {
    node.style.display = "flex";
    node.style.flexDirection = "row";
    node.style.alignItems = "flex-start";
    node.style.justifyContent = "space-between";
    node.style.width = "100%";
    node.style.maxWidth = "100%";
    node.style.padding = "20px";
    node.style.borderRadius = "12px";
    node.style.background = "#fafafa";
    node.style.boxSizing = "border-box";
    node.style.gap = "24px";
  });
}

function fixPublicAssetPaths(root) {
  root.querySelectorAll("img").forEach((img) => {
    const src = img.getAttribute("src");
    if (!src || /^(https?:|data:|blob:)/i.test(src)) {
      return;
    }

    if (src.startsWith("/")) {
      img.src = getPublicAssetUrl(src.slice(1));
      return;
    }

    try {
      const pathname = new URL(src, window.location.href).pathname.replace(
        /^\//,
        ""
      );
      img.src = getPublicAssetUrl(pathname);
    } catch (error) {
      console.warn("Unable to resolve image path for PDF export:", src, error);
      img.remove();
    }
  });
}

function sanitizeBrokenImagesForPdf(root) {
  root.querySelectorAll("img").forEach((img) => {
    if (!img.getAttribute("src")) {
      img.remove();
      return;
    }

    if (img.complete && img.naturalWidth === 0) {
      img.remove();
    }
  });

  root.querySelectorAll(".TeamFlagPlaceholder").forEach((placeholder) => {
    placeholder.style.display = "flex";
  });
}
